import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SbomService } from '../../../Services/sbom.service';
import { SbomProject, SbomProjectStatus, SbomProjectStatusLabels } from '../../../Models/sbom.model';
import { ConfirmDialogComponent } from '../../shared/confirm-dialog/confirm-dialog.component';
import { SbomProjectFormComponent } from '../sbom-project-form/sbom-project-form.component';

@Component({
  selector: 'app-sbom-list',
  templateUrl: './sbom-list.component.html',
  styleUrls: ['./sbom-list.component.css']
})
export class SbomListComponent implements OnInit {
  projects: SbomProject[] = [];
  filteredProjects: SbomProject[] = [];
  searchTerm = '';
  loading = true;
  statusLabels = SbomProjectStatusLabels;

  constructor(
    private sbomService: SbomService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.loadProjects();
  }

  loadProjects(): void {
    this.loading = true;
    this.sbomService.getProjects().subscribe({
      next: (projects) => {
        this.projects = projects ?? [];
        this.applyFilter();
        this.loading = false;
      },
      error: () => {
        this.snackBar.open('Fehler beim Laden der SBOM-Projekte', 'OK', { duration: 4000 });
        this.loading = false;
      }
    });
  }

  applyFilter(): void {
    const term = this.searchTerm.toLowerCase().trim();
    if (!term) {
      this.filteredProjects = [...this.projects];
    } else {
      this.filteredProjects = this.projects.filter(p =>
        `${p.name} ${p.customerName} ${p.projectNumber} ${p.plcType}`.toLowerCase().includes(term)
      );
    }
  }

  clearSearch(): void {
    this.searchTerm = '';
    this.applyFilter();
  }

  openProjectForm(project?: SbomProject): void {
    const dialogRef = this.dialog.open(SbomProjectFormComponent, {
      width: '640px',
      data: project ? { ...project } : null,
      autoFocus: true
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.loadProjects();
        this.snackBar.open(project ? 'Projekt aktualisiert' : 'Projekt erstellt', 'OK', { duration: 3000 });
      }
    });
  }

  openProject(project: SbomProject): void {
    this.router.navigate(['/sbom/projekt', project.id]);
  }

  deleteProject(project: SbomProject, event: Event): void {
    event.stopPropagation();
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '380px',
      data: { title: 'Projekt löschen', message: `"${project.name}" wirklich löschen? Alle Komponenten und Dokumente werden ebenfalls gelöscht.` }
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.sbomService.deleteProject(project.id).subscribe({
          next: () => {
            this.loadProjects();
            this.snackBar.open('Projekt gelöscht', 'OK', { duration: 3000 });
          },
          error: () => this.snackBar.open('Fehler beim Löschen', 'OK', { duration: 4000 })
        });
      }
    });
  }

  getStatusClass(status: SbomProjectStatus): string {
    switch (status) {
      case SbomProjectStatus.InPlanung: return 'status-planning';
      case SbomProjectStatus.InArbeit: return 'status-active';
      case SbomProjectStatus.Uebergeben: return 'status-handover';
      case SbomProjectStatus.Abgeschlossen: return 'status-done';
      case SbomProjectStatus.Archiviert: return 'status-archived';
      default: return '';
    }
  }

  get totalComponents(): number {
    return this.projects.reduce((sum, p) => sum + (p.componentCount || 0), 0);
  }

  get totalDocuments(): number {
    return this.projects.reduce((sum, p) => sum + (p.documentCount || 0), 0);
  }

  getLogoUrl(logoPath: string): string {
    return this.sbomService.getCustomerLogoUrl(logoPath);
  }

  startSecurityCheck(project: SbomProject, event: Event): void {
    event.stopPropagation();
    this.router.navigate(['/sbom/projekt', project.id, 'sicherheitscheck']);
  }

  getSecurityProgress(project: SbomProject): number {
    const a = project.securityAssessment;
    if (!a || a.totalRequirements === 0) return 0;
    return Math.round((a.completedRequirements / a.totalRequirements) * 100);
  }
}
